import { Component, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { DynamicFormComponent } from '../../../../shared/dynamic-form/dynamic-form.component';
import { FieldOptions } from '../../../../shared/dynamic-form/interfaces/field-options.interface';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { BarrierStatus, labelsByBarrierStatus } from '../../../../shared/models/enums/barrier-status.enum';
import { DiagnosticStatus, labelsByDiagnosticStatus } from '../../../../shared/models/enums/diagnostic-status.enum';
import { ResidentialStatusLabel } from '../../../../shared/models/enums/residential-status.enum';
import { AddressUserService } from '../../../../shared/services/addressUser.service';
import { AreaService } from '../../../../shared/services/area.service';
import { BarrierService } from '../../../../shared/services/barrier.service';
import { BeneficiariesService } from '../../../../shared/services/beneficiaries.service';
import { BeneficiaryEmploymentService } from '../../../../shared/services/beneficiary-employment.service';
import { BeneficiaryComplementsService } from '../../../../shared/services/beneficiaryComplements.service';
import { DateService } from '../../../../shared/services/date.service';
import { DegreeService } from '../../../../shared/services/degree.service';
import { DiagnosticService } from '../../../../shared/services/diagnostic.service';
import { PostalAddressService } from '../../../../shared/services/postal-address.service';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { UserService } from '../../../../shared/services/user.service';
import { ToastService } from '../../../../shared/toast/toast.service';
import { Utils } from '../../../../shared/utils';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryComplement } from '../../../beneficiary-details/complement/beneficiaryComplement';
import { BeneficiaryComplementSaveDto } from '../../../beneficiary-details/complement/beneficiaryComplementSaveDto';
import { Complement } from '../../../beneficiary-details/complement/complement';
import { SupportDto } from '../../../dto/supportDto';
import { BeneficiaryEmploymentDto } from '../../../beneficiary-details/employment/dto/beneficiary-employment.dto';
import { EmploymentBarrierDto } from '../../../beneficiary-details/employment/dto/employment-barrier.dto';
import { EmploymentQualificationDto } from '../../../beneficiary-details/employment/dto/employment-qualification.dto';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { DiagnosticInit } from '../diagnosticInit';
import { ChildrenDto } from './childrenDto';
import { DiagnosticDto } from './diagnosticDto';
import { DiagnosticQualificationDto } from './diagnosticQualificationDto';
import { FormationDto } from './formationDto';

@Component({
  selector: 'app-diagnostic-details',
  templateUrl: './diagnostic-details.component.html',
  styleUrls: ['./diagnostic-details.component.scss'],
})
export class DiagnosticDetailsComponent {
  public diagnosticDetailsForm: UntypedFormGroup;
  public submitted: boolean = false;

  public childrenFields: (string | FieldOptions)[] = [];
  public formationFields: (string | FieldOptions)[] = [];

  public drivingsDesc: string;
  public drivingsDescDiagnostic: string;
  public barriers: EmploymentBarrierDto[];
  public barriersById: Map<number, DisplayLabel> = new Map<number, DisplayLabel>();
  public utils: Utils = Utils;

  public readonly diagnosticStatusMap: Map<DiagnosticStatus, string> = labelsByDiagnosticStatus;
  public readonly barrierStatus: Map<BarrierStatus, string> = labelsByBarrierStatus;
  public readonly StringUtils: StringUtils = StringUtils;
  public groupValidators: ValidatorFn[] = [];
  public readonly residentialStatusEnum = ResidentialStatusLabel;

  public diagnosticInit: DiagnosticInit | undefined = undefined;
  public diagnostic: DiagnosticDto | undefined = undefined;
  private referent: UserDto;
  private readonly currentDate: Date;
  private beneficiaryId: number;
  public isConsultMode: boolean = false;

  public degreesList: any[] = [];
  public user: UserDto | undefined = undefined;
  private readonly destroy$: Subject<void> = new Subject();

  public structureName: string = '';
  public structureTel: string;
  public clis: DisplayLabel[] = [];
  public degreeId: number;

  public beneficiary: BeneficiaryDto;
  public beneficiaryEmployment: BeneficiaryEmploymentDto;
  public beneficiaryComplement: BeneficiaryComplement;

  private supportHistoriesIdToDelete: Array<number> = [];
  private supportHistoriesToAdd: Array<SupportDto> = [];

  public qualificationEmployment;

  @ViewChild('children', { static: false }) public children: DynamicFormComponent;
  @ViewChild('formation', { static: false }) public formation: DynamicFormComponent;

  constructor(
    private readonly modal: NgbActiveModal,
    private readonly diagnosticService: DiagnosticService,
    private readonly barrierService: BarrierService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly dateService: DateService,
    private readonly toastService: ToastService,
    private readonly userService: UserService,
    private readonly modalService: NgbModal,
    private degree: DegreeService,
    private readonly authService: AuthService,
    private readonly areaService: AreaService,
    private readonly beneficiaryEmploymentService: BeneficiaryEmploymentService,
    private readonly beneficiaryService: BeneficiariesService,
    private readonly beneficiaryComplementService: BeneficiaryComplementsService,
    private readonly postalAddressService: PostalAddressService,
    private readonly adresseUserService: AddressUserService,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  public async onInit(beneficiaryId: number, diagnosticId?: number, diagnosticInit?: DiagnosticInit): Promise<void> {
    this.beneficiaryId = beneficiaryId;
    this.diagnosticInit = diagnosticInit;

    this.diagnostic = diagnosticId ? await this.diagnosticService.findByDiagnosticId(diagnosticId) : undefined;

    await this.getUser();
    await this.initListDegree();
    await this.loadForm();

    this.barriersById = await this.barrierService.findAllById();

    this.childrenFields = this.getChildrenFields();
    this.formationFields = this.getFormationFields();
    this.groupValidators = this.getGroupValidators();

    // get beneficiary by id
    this.beneficiary = await this.beneficiaryService.findById(this.beneficiaryId);

    // get beneficiaryEmployement if exists, else create new one
    if (this.beneficiary.employmentId) {
      this.beneficiaryEmployment = await this.beneficiaryEmploymentService.findById(this.beneficiary?.employmentId);
    } else {
      this.beneficiaryEmployment = new BeneficiaryEmploymentDto({
        id: null,
        accreditationSIAE: null,
        accreditationSIAEDate: null,
        rqth: null,
        expiryRqthDate: null,
        entrepreneur: null,
        beginDateEntrepreneurship: null,
        endDateEntrepreneurship: null,
        sectorId: null,
        degreeId: null,
        trainingAreaIds: [],
        wantedJobTypeId: null,
        studyAbroad: undefined,
        qualificationTypes: [],
        transportModes: [],
        barriers: [],
        assets: [],
        jobIds: [],
      });
    }
    // get beneficiaryComplement if exists, else create new one
    if (this.beneficiary.complementId) {
      this.beneficiaryComplement = await this.beneficiaryComplementService.findById(this.beneficiary.complementId);
    } else {
      this.beneficiaryComplement = new BeneficiaryComplement(null, null, null, null, null, null, null);
    }
  }

  private async getUser(): Promise<void> {
    this.user = await this.authService.getUser();
  }

  private async loadForm(): Promise<void> {
    if (!this.diagnostic) {
      this.drivingsDesc = this.diagnosticInit.drivingLicenses.map((item: DiagnosticQualificationDto) => item.qualificationDesc).toString();
      this.barriers = this.diagnosticInit.barriers;
      this.diagnostic = {} as DiagnosticDto;
      this.diagnosticDetailsForm = this.formBuilder.group({
        beginDateOrientation: [this.diagnosticInit.beginDateOrientation, [Validators.required, DateValidators.dateFormatValidator]],
        diagnosticStatus: [undefined, [Validators.required]],
        statusDate: [{ value: undefined, disabled: true }],
        diagnosticForm: this.formBuilder.group(
          {
            title: [{ value: this.diagnosticInit.title, disabled: true }],
            usualName: [{ value: this.diagnosticInit.usualName, disabled: true }],
            firstName: [{ value: this.diagnosticInit.firstName, disabled: true }],
            birthDate: [{ value: this.diagnosticInit.birthDate, disabled: true }],
            address: [{ value: this.diagnosticInit.address, disabled: true }],
            additionalAddress: [{ value: this.diagnosticInit.additionalAddress, disabled: true }],
            city: [{ value: this.diagnosticInit.city, disabled: true }],
            postalCode: [{ value: this.diagnosticInit.postalCode, disabled: true }],
            residentialStatus: [{ value: this.diagnosticInit.residentialStatus, disabled: true }],
            residentialStatusComplement: [{ value: this.diagnosticInit.residentialStatusComplement, disabled: true }],
            maritalStatus: [this.diagnosticInit.maritalStatus, [Validators.required]],
            spouseAge: [undefined, [Validators.pattern('[0-9]*')]],
            spouseOccupation: [undefined],
            jobCenterRegistration: [this.diagnosticInit.jobCenterRegistration],
            RSA: [this.diagnosticInit.RSA],
            RQTH: [this.diagnosticInit.RQTH],
            mission: [undefined],
            natureLastJob: [undefined],
            beginDateLastJob: [undefined, [DateValidators.dateFormatValidator]],
            endDateLastJob: [undefined, [DateValidators.dateFormatValidator]],
            degreeLabel: [this.diagnosticInit.degreeLabel],
            highestDegree: [undefined],
            drivingLicense: [this.diagnosticInit.drivingLicense ? 'yes' : 'no'],
            skills: [undefined],
          },
          {
            validators: [DateValidators.datesOrderValidator('beginDateLastJob', 'endDateLastJob')],
          },
        ),
        prescriptionForm: this.formBuilder.group({
          structureLabel: [{ value: this.diagnosticInit.structureLabel, disabled: true }],
          structureAddress: [{ value: this.diagnosticInit.structureAddress, disabled: true }],
          structureAdditionalAddress: [{ value: this.diagnosticInit.structureAdditionalAddress, disabled: true }],
          structureCity: [{ value: this.diagnosticInit.structureCity, disabled: true }],
          structurePostalCode: [{ value: this.diagnosticInit.structurePostalCode, disabled: true }],
          structurePhone: [{ value: this.diagnosticInit.structurePhone, disabled: true }],
          referentName: [{ value: this.diagnosticInit.referentName, disabled: true }],
          referentPhone: [{ value: this.diagnosticInit.referentPhone, disabled: true }],
          project: [undefined],
          analysis1: [undefined],
          analysis2: [undefined],
          analysis3: [undefined],
          analysis4: [undefined],
          analysis5: [undefined],
          analysis6: [undefined],
        }),
      });
    } else {
      this.isConsultMode = true;
      this.barriers = this.diagnostic.barriers;
      this.referent = this.diagnostic.referentId ? await this.userService.findUser(this.diagnostic.referentId) : undefined;
      this.drivingsDescDiagnostic = this.diagnostic.drivingLicenses.map((item: DiagnosticQualificationDto) => item.qualificationDesc).toString();
      const mainAddress = this.diagnostic?.structure?.addresses.find((addr) => addr.main)
        ? this.diagnostic.structure.addresses.find((addr) => addr.main)
        : null;
      this.diagnosticDetailsForm = this.formBuilder.group({
        beginDateOrientation: [this.diagnostic.beginDateOrientation, [Validators.required, DateValidators.dateFormatValidator]],
        diagnosticStatus: [this.diagnostic.diagnosticStatus, [Validators.required]],
        statusDate: [{ value: this.diagnostic.statusDate, disabled: true }],
        diagnosticForm: this.formBuilder.group(
          {
            title: [{ value: this.diagnostic.title, disabled: true }],
            usualName: [{ value: this.diagnostic.usualName, disabled: true }],
            firstName: [{ value: this.diagnostic.firstName, disabled: true }],
            birthDate: [{ value: this.diagnostic.birthDate, disabled: true }],
            address: [{ value: this.diagnostic.address, disabled: true }],
            additionalAddress: [{ value: this.diagnostic.additionalAddress, disabled: true }],
            city: [{ value: this.diagnostic.city, disabled: true }],
            postalCode: [{ value: this.diagnostic.postalCode, disabled: true }],
            residentialStatus: [{ value: this.diagnostic.residentialStatus, disabled: true }],
            residentialStatusComplement: [{ value: this.diagnostic.residentialStatusComplement, disabled: true }],
            maritalStatus: [this.diagnostic.maritalStatus],
            spouseAge: [this.diagnostic.spouseAge, [Validators.pattern('[0-9]*')]],
            spouseOccupation: [this.diagnostic.spouseOccupation],
            jobCenterRegistration: [this.diagnostic.jobCenterRegistration],
            RSA: [this.diagnostic.RSA],
            RQTH: [this.diagnostic.RQTH],
            mission: [this.diagnostic.mission],
            natureLastJob: [this.diagnostic.natureLastJob],
            beginDateLastJob: [this.diagnostic.beginDateLastJob, [DateValidators.dateFormatValidator]],
            endDateLastJob: [this.diagnostic.endDateLastJob, [DateValidators.dateFormatValidator]],
            degreeLabel: [{ value: this.diagnostic.degreeLabel }],
            highestDegree: [this.diagnostic.highestDegree],
            drivingLicense: [{ value: this.diagnostic.drivingLicense ? 'yes' : 'no' }],
            skills: [this.diagnostic.skills],
          },
          {
            validators: [DateValidators.datesOrderValidator('beginDateLastJob', 'endDateLastJob')],
          },
        ),
        prescriptionForm: this.formBuilder.group({
          structureLabel: [{ value: this.diagnostic.structure?.label, disabled: true }],
          structureAddress: [{ value: mainAddress?.address, disabled: true }],
          structureAdditionalAddress: [{ value: mainAddress?.additionalAddress, disabled: true }],
          structureCity: [{ value: mainAddress?.city, disabled: true }],
          structurePostalCode: [{ value: mainAddress?.postalCode, disabled: true }],
          structurePhone: [{ value: mainAddress?.phone, disabled: true }],
          referentName: [{ value: this.referent?.name, disabled: true }],
          referentPhone: [
            {
              value:
                this.referent && this.referent.postalAddresses && this.referent.postalAddresses.length > 0
                  ? this.referent.postalAddresses[0].telephone
                  : undefined,
              disabled: true,
            },
          ],
          project: [this.diagnostic.project],
          analysis1: [this.diagnostic.analysis1],
          analysis2: [this.diagnostic.analysis2],
          analysis3: [this.diagnostic.analysis3],
          analysis4: [this.diagnostic.analysis4],
          analysis5: [this.diagnostic.analysis5],
          analysis6: [this.diagnostic.analysis6],
        }),
      });
      this.diagnosticForm.controls.degreeLabel.setValue(this.diagnostic.degreeLabel);
      this.diagnosticForm.controls.drivingLicense.setValue(this.diagnostic.drivingLicense ? 'yes' : 'no');
    }
  }

  private async initListDegree(): Promise<void> {
    this.degreesList = await this.degree.findAll();
  }
  public get diagnosticForm(): UntypedFormGroup {
    return this.diagnosticDetailsForm.controls.diagnosticForm as UntypedFormGroup;
  }

  public get prescriptionForm(): UntypedFormGroup {
    return this.diagnosticDetailsForm.controls.prescriptionForm as UntypedFormGroup;
  }

  public onStatusChange(): void {
    this.diagnosticDetailsForm.controls.statusDate.setValue(this.currentDate);
    this.diagnosticDetailsForm.get('statusDate').updateValueAndValidity();
  }
  private async getBeneficiaryComplement(idComplement: number): Promise<void> {
    this.beneficiaryComplement = await this.beneficiaryComplementService.findById(idComplement);
  }

  private async updateComplement(complement: Complement) {
    const beneficiaryComplementId: number = await this.beneficiaryComplementService.updateComplementBeneficiary(this.beneficiary.id, complement);
    await this.getBeneficiaryComplement(beneficiaryComplementId);
    this.supportHistoriesToAdd = [];
    this.supportHistoriesIdToDelete = [];
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;

    // get degree Ids
    this.degreesList.map((value) => {
      if (value.label === this.diagnosticForm.controls.degreeLabel.value) {
        this.degreeId = value.id;
      }
    });

    if (
      !this.diagnosticDetailsForm.valid ||
      !this.diagnosticForm.valid ||
      !this.prescriptionForm.valid ||
      !this.children.form.valid ||
      !this.formation.form.valid
    ) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
    }
    if (this.diagnosticInit) {
      let complementBeneficiaryUpdated: BeneficiaryComplementSaveDto = {
        maritalStatus: this.diagnosticInit.maritalStatus ? this.diagnosticInit.maritalStatus : this.diagnosticForm.controls.maritalStatus.value,
        status: this.beneficiary.status,
        childrenCount: Number(this.beneficiaryComplement.childrenCount),
        dependentsNumber: Number(this.beneficiaryComplement.dependentsNumber),
        healthInsuranceId: this.beneficiaryComplement.healthInsuranceId,
        singleParent: this.beneficiaryComplement.singleParent,
        beginDateSupport: this.beneficiary.support.beginDate,
        endDateSupport: this.beneficiary.support.endDate,
        supportTypeId: this.beneficiary.support.supportType.id,
        typeSupportDetail: this.beneficiary.support.typeSupportDetail,
      } as BeneficiaryComplementSaveDto;

      complementBeneficiaryUpdated = Utils.normalizeStrings(complementBeneficiaryUpdated);

      const complement: Complement = {
        beneficiaryComplement: complementBeneficiaryUpdated,
        supportToDelete: this.supportHistoriesIdToDelete,
        supportToAdd: this.supportHistoriesToAdd,
        referents: [],
      } as Complement;
      await this.updateComplement(complement);

      // add employement qualification if empty
      if (this.diagnosticInit.drivingLicense) {
        this.qualificationEmployment = this.beneficiaryEmployment.qualificationTypes;
      } else {
        this.updateEmploymentQualificationIfDrivingLicence();
      }

      // fill dto  employmentUpdated to save values : degreId, RQTH and qualificationTypes in the employment part
      const employmentUpdated: BeneficiaryEmploymentDto = new BeneficiaryEmploymentDto({
        id: this.beneficiaryEmployment.id,
        accreditationSIAE: this.beneficiaryEmployment.accreditationSIAE,
        accreditationSIAEDate: this.beneficiaryEmployment.accreditationSIAEDate,
        rqth: this.diagnosticInit.RQTH ? this.diagnosticInit.RQTH : this.diagnosticForm.controls.RQTH.value,
        expiryRqthDate: this.beneficiaryEmployment.expiryRqthDate,
        entrepreneur: this.beneficiaryEmployment.entrepreneur,
        beginDateEntrepreneurship: this.beneficiaryEmployment.beginDateEntrepreneurship,
        endDateEntrepreneurship: this.beneficiaryEmployment.endDateEntrepreneurship,
        sectorId: this.beneficiaryEmployment.sectorId,
        degreeId: this.diagnosticInit.degreeId ? this.diagnosticInit.degreeId : this.degreeId,
        trainingAreaIds: this.beneficiaryEmployment.trainingAreaIds,
        wantedJobTypeId: this.beneficiaryEmployment.wantedJobTypeId,
        studyAbroad: this.beneficiaryEmployment.studyAbroad,
        qualificationTypes: this.qualificationEmployment,
        transportModes: this.beneficiaryEmployment.transportModes,
        barriers: this.beneficiaryEmployment.barriers,
        assets: this.beneficiaryEmployment.assets,
        jobIds: this.beneficiaryEmployment.jobIds,
      });

      await this.beneficiaryEmploymentService.updateEmploymentBeneficiary(this.beneficiary.id, employmentUpdated);
    }

    let diagnosticDto: DiagnosticDto;
    diagnosticDto = {
      id: this.diagnostic.id,
      idBeneficiary: this.beneficiaryId,
      beginDateOrientation: this.diagnosticDetailsForm.controls.beginDateOrientation.value,
      diagnosticStatus: this.diagnosticDetailsForm.controls.diagnosticStatus.value,
      statusDate: this.diagnosticDetailsForm.controls.statusDate.value,
      title: this.diagnosticForm.controls.title.value,
      usualName: this.diagnosticForm.controls.usualName.value,
      firstName: this.diagnosticForm.controls.firstName.value,
      birthDate: this.diagnosticForm.controls.birthDate.value,
      address: this.diagnosticForm.controls.address.value,
      additionalAddress: this.diagnosticForm.controls.additionalAddress.value,
      city: this.diagnosticForm.controls.city.value,
      postalCode: this.diagnosticForm.controls.postalCode.value,
      residentialStatus: this.diagnosticForm.controls.residentialStatus.value,
      residentialStatusComplement: this.diagnosticForm.controls.residentialStatusComplement.value,
      maritalStatus: this.diagnosticForm.controls.maritalStatus.value,
      spouseAge: this.diagnosticForm.controls.spouseAge.value,
      spouseOccupation: this.diagnosticForm.controls.spouseOccupation.value,
      jobCenterRegistration: this.diagnosticForm.controls.jobCenterRegistration.value,
      RSA: this.diagnosticForm.controls.RSA.value,
      RQTH: this.diagnosticForm.controls.RQTH.value,
      mission: this.diagnosticForm.controls.mission.value,
      natureLastJob: this.diagnosticForm.controls.natureLastJob.value,
      beginDateLastJob: this.diagnosticForm.controls.beginDateLastJob.value,
      endDateLastJob: this.diagnosticForm.controls.endDateLastJob.value,
      degreeId: this.degreeId,
      degreeLabel: this.diagnosticForm.controls.degreeLabel.value,
      highestDegree: this.diagnosticForm.controls.highestDegree.value,
      drivingLicense: this.diagnosticForm.controls.drivingLicense.value === 'yes' ? true : false,
      drivingLicenses: this.diagnosticInit?.drivingLicenses ? this.diagnosticInit.drivingLicenses : this.diagnostic.drivingLicenses,
      skills: this.diagnosticForm.controls.skills.value,
      barriers: this.diagnosticInit?.barriers ? this.diagnosticInit.barriers : this.diagnostic.barriers,
      structureId: this.diagnosticInit?.structureId ? this.diagnosticInit.structureId : this.diagnostic.structureId,
      structure: this.diagnostic?.structure,
      referentId: this.user.id,
      project: this.prescriptionForm.controls.project.value,
      analysis1: this.prescriptionForm.controls.analysis1.value,
      analysis2: this.prescriptionForm.controls.analysis2.value,
      analysis3: this.prescriptionForm.controls.analysis3.value,
      analysis4: this.prescriptionForm.controls.analysis4.value,
      analysis5: this.prescriptionForm.controls.analysis5.value,
      analysis6: this.prescriptionForm.controls.analysis6.value,
      children: this.fillChildren(),
      formations: this.fillFormation(),
    } as DiagnosticDto;

    await this.diagnosticService.saveDiagnostic(diagnosticDto);
    this.toastService.show('Le diagnostic a été sauvegardé avec succès.', 'success');
    this.modal.close();
  }

  private updateEmploymentQualificationIfDrivingLicence() {
    if (this.diagnosticForm.controls.drivingLicense.value === 'yes') {
      const employmentQualification: EmploymentQualificationDto = new EmploymentQualificationDto(
        null,
        this.diagnosticInit.birthDate,
        this.drivingsDesc,
        1,
      );

      let employmentQalificationList: any[] = [];
      employmentQalificationList.push(employmentQualification);

      this.qualificationEmployment = employmentQalificationList;
    }
  }

  public cancel(): void {
    this.modal.close();
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.modal.close();
      await this.diagnosticService.delete(this.diagnostic.id);
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce diagnostic ?');
  }

  private fillChildren(): ChildrenDto[] {
    const children: ChildrenDto[] = [];
    for (let i: number = 0; i < this.children.form.controls.items.value.length; i++) {
      const child: ChildrenDto = new ChildrenDto(
        this.children.form.controls.items.value[i].id,
        this.children.form.controls.items.value[i].childAge,
        this.children.form.controls.items.value[i].childOccupation,
      );
      children.push(child);
    }
    return children;
  }

  private fillFormation(): FormationDto[] {
    const formations: FormationDto[] = [];
    for (let i: number = 0; i < this.formation.form.controls.items.value.length; i++) {
      const formation: FormationDto = new FormationDto(
        this.formation.form.controls.items.value[i].id,
        this.formation.form.controls.items.value[i].trainingTitle,
        Utils.isNotEmpty(this.formation.form.controls.items.value[i].beginDateFormation)
          ? this.formation.form.controls.items.value[i].beginDateFormation
          : undefined,
        Utils.isNotEmpty(this.formation.form.controls.items.value[i].endDateFormation)
          ? this.formation.form.controls.items.value[i].endDateFormation
          : undefined,
      );
      formations.push(formation);
    }
    return formations;
  }

  private getChildrenFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'childAge', validators: [Validators.required, Validators.pattern('[0-9]*')] },
      { value: 'childOccupation', validators: [] },
    ];
  }

  private getFormationFields(): (string | FieldOptions)[] {
    return [
      { value: 'id', validators: [] },
      { value: 'trainingTitle', validators: [Validators.required] },
      { value: 'beginDateFormation', validators: [DateValidators.dateFormatValidator] },
      { value: 'endDateFormation', validators: [DateValidators.dateFormatValidator] },
    ];
  }

  private getGroupValidators(): ValidatorFn[] {
    return [DateValidators.datesOrderValidator('beginDateFormation', 'endDateFormation')];
  }

  public async downloadDocument(): Promise<void> {
    await this.diagnosticService.downloadDocument(this.diagnostic.id);
  }

  public async syncDiagnostic(): Promise<void> {
    this.diagnosticInit = await this.diagnosticService.findByBeneficiaryId(this.beneficiaryId);
    this.diagnosticForm.controls.title.setValue(this.diagnosticInit.title);
    this.diagnosticForm.controls.usualName.setValue(this.diagnosticInit.usualName);
    this.diagnosticForm.controls.firstName.setValue(this.diagnosticInit.firstName);
    this.diagnosticForm.controls.birthDate.setValue(this.diagnosticInit.birthDate);
    this.diagnosticForm.controls.address.setValue(this.diagnosticInit.address);
    this.diagnosticForm.controls.additionalAddress.setValue(this.diagnosticInit.additionalAddress);
    this.diagnosticForm.controls.city.setValue(this.diagnosticInit.city);
    this.diagnosticForm.controls.postalCode.setValue(this.diagnosticInit.postalCode);
    this.diagnosticForm.controls.residentialStatus.setValue(this.diagnosticInit.residentialStatus);
    this.diagnosticForm.controls.residentialStatusComplement.setValue(this.diagnosticInit.residentialStatusComplement);
    this.diagnosticForm.controls.maritalStatus.setValue(this.diagnosticInit.maritalStatus);
    this.diagnosticForm.controls.jobCenterRegistration.setValue(this.diagnosticInit.jobCenterRegistration);
    this.diagnosticForm.controls.RSA.setValue(this.diagnosticInit.RSA);
    this.diagnosticForm.controls.RQTH.setValue(this.diagnosticInit.RQTH);
    this.diagnosticForm.controls.degreeLabel.setValue(this.diagnosticInit.degreeLabel);
    this.diagnosticForm.controls.drivingLicense.setValue(this.diagnosticInit.drivingLicense ? 'yes' : 'no');
    this.barriers = this.diagnosticInit.barriers;
    this.drivingsDesc = this.diagnosticInit.drivingLicenses.map((item: DiagnosticQualificationDto) => item.qualificationDesc).toString();

    this.prescriptionForm.controls.structureLabel.setValue(this.structureName);
    this.prescriptionForm.controls.structureAddress.setValue(this.diagnosticInit.structureAddress);
    this.prescriptionForm.controls.structureAdditionalAddress.setValue(this.diagnosticInit.structureAdditionalAddress);
    this.prescriptionForm.controls.structureCity.setValue(this.diagnosticInit.structureCity);
    this.prescriptionForm.controls.structurePostalCode.setValue(this.diagnosticInit.structurePostalCode);
    this.prescriptionForm.controls.structurePhone.setValue(this.diagnosticInit.structurePhone);
    this.prescriptionForm.controls.referentName.setValue(this.diagnosticInit.referentName);
    this.prescriptionForm.controls.referentPhone.setValue(this.diagnosticInit.referentPhone);
  }
}
